import axios from 'axios'
import * as ApiSettingService from "src/db/serviceApiSetting"
import { env } from "src/config/env"

const BASE_URL = env.SERVER_URL

export type ProcessStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'

export interface ProcessTrackingResponse {
  process_id: string
  status: ProcessStatus
  total_products: number
  processed_count?: number
  success_count?: number
  failed_count?: number
  error_message?: string
  started_at?: string
  completed_at?: string
  estimated_completion?: string
}

export interface ProcessTrackingConfig {
  pollInterval: number
  maxPollAttempts: number
  timeoutMs: number
}

export class ProcessTrackingService {
  private static instance: ProcessTrackingService
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map()
  private abortControllers: Map<string, AbortController> = new Map()

  static getInstance(): ProcessTrackingService {
    if (!this.instance) {
      this.instance = new ProcessTrackingService()
    }
    return this.instance
  }

  private defaultConfig: ProcessTrackingConfig = {
    pollInterval: 2000, // 2 seconds
    maxPollAttempts: 150, // 5 minutes with 2-second intervals
    timeoutMs: 300000 // 5 minutes total timeout
  }

  async getProcessStatus(processId: string): Promise<ProcessTrackingResponse> {
    try {
      const accountAccess = await ApiSettingService.getAccess()
      if (!accountAccess) {
        throw new Error("Authentication failed - unable to get access token")
      }

      const response = await axios.get<ProcessTrackingResponse>(
        `${BASE_URL}/api/v2/process/${processId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accountAccess.accessToken}`,
          },
        }
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Process not found - it may have expired or been removed')
        }
        if (error.response?.status === 401) {
          throw new Error('Authentication failed - please check your credentials')
        }
      }
      
      console.error(`Error fetching process status for ${processId}:`, error)
      throw error instanceof Error ? error : new Error('Failed to get process status')
    }
  }

  async pollProcessStatus(
    processId: string,
    onUpdate: (status: ProcessTrackingResponse) => void,
    onComplete: (status: ProcessTrackingResponse) => void,
    onError: (error: Error) => void,
    config: Partial<ProcessTrackingConfig> = {}
  ): Promise<void> {
    const finalConfig = { ...this.defaultConfig, ...config }
    const startTime = Date.now()
    let attempts = 0

    // Clean up any existing polling for this process
    this.stopPolling(processId)

    // Create abort controller for this polling operation
    const abortController = new AbortController()
    this.abortControllers.set(processId, abortController)

    const poll = async (): Promise<void> => {
      if (abortController.signal.aborted) {
        return
      }

      attempts++
      const elapsed = Date.now() - startTime

      // Check timeout
      if (elapsed > finalConfig.timeoutMs) {
        this.stopPolling(processId)
        onError(new Error(`Process tracking timed out after ${finalConfig.timeoutMs / 1000} seconds`))
        return
      }

      // Check max attempts
      if (attempts > finalConfig.maxPollAttempts) {
        this.stopPolling(processId)
        onError(new Error(`Process tracking stopped after ${finalConfig.maxPollAttempts} attempts`))
        return
      }

      try {
        const status = await this.getProcessStatus(processId)
        onUpdate(status)

        // Check if process is complete
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          this.stopPolling(processId)
          onComplete(status)
          return
        }

        // Schedule next poll if still processing
        if (status.status === 'queued' || status.status === 'processing') {
          const timeoutId = setTimeout(poll, finalConfig.pollInterval)
          this.pollingIntervals.set(processId, timeoutId)
        }
      } catch (error) {
        // For temporary network errors, continue polling with exponential backoff
        if (attempts < 3 || (axios.isAxiosError(error) && error.code === 'ECONNABORTED')) {
          const backoffDelay = Math.min(finalConfig.pollInterval * Math.pow(2, Math.min(attempts - 1, 3)), 10000)
          const timeoutId = setTimeout(poll, backoffDelay)
          this.pollingIntervals.set(processId, timeoutId)
          return
        }
        
        // For persistent errors, stop polling
        this.stopPolling(processId)
        onError(error instanceof Error ? error : new Error('Unknown error occurred while tracking process'))
      }
    }

    // Start polling
    poll()
  }

  stopPolling(processId: string): void {
    // Clear polling interval
    const intervalId = this.pollingIntervals.get(processId)
    if (intervalId) {
      clearTimeout(intervalId)
      this.pollingIntervals.delete(processId)
    }

    // Abort any ongoing requests
    const abortController = this.abortControllers.get(processId)
    if (abortController) {
      abortController.abort()
      this.abortControllers.delete(processId)
    }
  }

  stopAllPolling(): void {
    // Stop all active polling operations
    for (const processId of this.pollingIntervals.keys()) {
      this.stopPolling(processId)
    }
  }

  isPolling(processId: string): boolean {
    return this.pollingIntervals.has(processId)
  }

  async trackPriceUpdateProcess(
    processId: string,
    onProgress: (status: ProcessTrackingResponse) => void,
    onSuccess: (status: ProcessTrackingResponse) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    return this.pollProcessStatus(
      processId,
      (status) => {
        onProgress(status)
      },
      (status) => {
        if (status.status === 'completed') {
          onSuccess(status)
        } else if (status.status === 'failed') {
          onError(new Error(status.error_message || 'Price update process failed'))
        } else if (status.status === 'cancelled') {
          onError(new Error('Price update process was cancelled'))
        }
      },
      onError
    )
  }

  getProgressPercentage(status: ProcessTrackingResponse): number {
    if (status.total_products === 0) return 0
    if (!status.processed_count) return 0
    
    return Math.round((status.processed_count / status.total_products) * 100)
  }

  formatProcessDuration(startedAt?: string, completedAt?: string): string {
    if (!startedAt) return 'Not started'
    
    const start = new Date(startedAt)
    const end = completedAt ? new Date(completedAt) : new Date()
    const durationMs = end.getTime() - start.getTime()
    const seconds = Math.floor(durationMs / 1000)
    
    if (seconds < 60) {
      return `${seconds}s`
    }
    
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    
    if (remainingSeconds === 0) {
      return `${minutes}m`
    }
    
    return `${minutes}m ${remainingSeconds}s`
  }

  cleanup(): void {
    this.stopAllPolling()
  }
}

export default ProcessTrackingService